// Priceupdatemanager.types.ts

export type PriceUpdateStatus = 'Pending' | 'Updated' | 'Failed';

export interface PriceUpdateTrade {
  id: string;
  trade_date: string;
  marker_tas_type: string;
  book: string;
  commodity: string;
  pricing_from: string;
  symbol_code: string;
  quantity: number;
  paper_price: number;
  mtm: number;
  api_price: number;
  expected_price: number;
  trade_id: string;
  leg_id: string;
  process_status: string;
  status: string;
  is_TAS_MM_Updated: boolean;
  price_update_status: PriceUpdateStatus;
}

export interface PriceUpdateMechanismSummary {
  marker_tas_type: string;
  total: number;
  pending: number;
  processed: {
    total: number;
    updated: number;
    not_updated: number;
    not_in_paper: number;
  };
}

export interface PriceUpdateListResponse {
  status: boolean;
  date: string;
  filters: Record<string, unknown>;
  count: number;
  summary: PriceUpdateMechanismSummary[];
  grand_total: PriceUpdateMechanismSummary;
  price_update_status_choices: string[];
  info: PriceUpdateTrade[];
}

export interface PriceUpdateSummary {
  total: number;
  pending: number;
  updated: number;
  failed: number;
}

export const deriveSummary = (data: PriceUpdateListResponse): PriceUpdateSummary => {
  const { total, pending, processed } = data.grand_total;
  const updated = processed.updated;
  const failed = Math.max(total - pending - updated, 0);
  return { total, pending, updated, failed };
};

export const DATE_PRESET_OPTIONS = [
  'Today',
  'Yesterday',
  'This Week',
  'This Month',
  'Previous Month',
  'Custom',
] as const;

export type DatePresetType = typeof DATE_PRESET_OPTIONS[number];

export interface PriceUpdateFilters {
  datePreset: DatePresetType;
  fromDate: string | null;
  toDate: string | null;
  pricingMechanism: string[];
  priceUpdateStatus: string[];
  symbolCode: string;
  commodity: string[];
  tradeId: string;
  book: string[];
}

export const DEFAULT_FILTERS: PriceUpdateFilters = {
  datePreset: 'Today',
  fromDate: null,
  toDate: null,
  pricingMechanism: [],
  priceUpdateStatus: ['Pending', 'Failed'],
  commodity: [],
  symbolCode: '',
  tradeId: '',
  book: [],
};

export interface UpdateTasPriceResultItem {
  trade_id: string;
  status: 'Updated' | 'Failed';
  price?: number;
  failure_reason?: string;
}

export interface ManualPricePayload {
  id: string;
  price: number;
}

export interface ManualPriceUpdateResult {
  index: number;
  id: string | null;
  status: 'UPDATED' | 'SKIPPED' | 'FAILED';
  message: string;
}

export interface ManualPriceUpdateResponse {
  status: boolean;
  message: string;
  summary: {
    received: number;
    updated: number;
    skipped: number;
    pnl_updated: number;
    pnl_failed: number;
    pnl_skipped: number;
  };
  results: ManualPriceUpdateResult[];
}

export interface FuturesContract {
  id: string;
  contract_name: string;
}

export interface TasUpdateResponse {
  status: string;
  info: unknown[];
  'Total TAS': number;
  'Processed TAS': number;
  'Pending TAS': number;
  'Total TAS Updated': number;
  updated: number;
  flag_set_true: number;
  flag_set_false: number;
  no_api_match: number;
}

export const STATUS_SORT_ORDER: Record<PriceUpdateStatus, number> = {
  Pending: 0,
  Failed: 1,
  Updated: 2,
};

export const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending: { bg: '#ff4e00', color: '#FFFFFF' },
  rejected: { bg: '#C62828', color: '#FFFFFF' },
  validated: { bg: '#2E7D32', color: '#FFFFFF' },
  committed: { bg: '#1565C0', color: '#FFFFFF' },
  updated: { bg: '#2E7D32', color: '#FFFFFF' },
  failed: { bg: '#C62828', color: '#FFFFFF' },
};

export const DEFAULT_STATUS_COLOR = { bg: '#616161', color: '#FFFFFF' };
export interface TasAuditLogRow {
  'Logged At': string;
  'User': string;
  'Updation Type': string;
  'Trade Date': string;
  'Marker/TAS Type': string;
  'Book': string;
  'Commodity': string;
  'Pricing From': string;
  'Symbol': string;
  'Price': string;
  'MTM': string;
  'API Price': string;
  'Expected Price': string;
  'Trade ID': string;
  'Leg ID': string;
  'TAS/MM Updated': string;
  'Price Update Status': string;
  'PnL Status': string;
  'PnL Message': string;
}

export interface TasAuditReportResponse {
  status: boolean;
  date: string;
  summary: string[];
  columns: string[];
  count: number;
  data: TasAuditLogRow[];
}